// ---------------------------------------------------------------------------
/**
 * "Is the server still the same body of data it was yesterday?"
 *
 * The server carries a data epoch — a meaningless id that changes only when a
 * backup is restored (see backend/src/lib/appMeta.ts). This device remembers
 * the one it last synced with. They differ ⇒ the server was rolled back, and
 * the copy in this browser is from a future that no longer exists.
 *
 * It cannot be kept: replication pulls by an `(updated_at, id)` cursor, so a
 * device whose cursor sits past the restored data pulls nothing, notices
 * nothing, and pushes its stale documents back over the restore at the next
 * edit. The only answer is `resetLocalData()` and a sync from scratch.
 *
 * Deliberately quiet in every other case: no server answer (offline, a server
 * without the endpoint, a migration not yet applied) means NOTHING happens. A
 * device in the field must start on a hilltop with no signal.
 */
// ---------------------------------------------------------------------------

import { defineStoredString } from './storage';

/** The epoch this device last accepted as the one it is in step with. */
const storedEpoch = defineStoredString({ key: 'data_epoch' });

/** How long the check may delay the start before we get on with it. */
const TIMEOUT_MS = 5000;

export type EpochAction =
    /** Nothing to do: same epoch, or nothing to compare against. */
    | 'none'
    /** First contact — remember it, keep the local data. */
    | 'store'
    /** The server was restored: this device's copy has to go. */
    | 'reset';

/**
 * The whole rule, as one pure function. `null` from the server is not evidence
 * of anything and must never lead to a wipe; that asymmetry is the point.
 */
export function decideEpochAction(
    stored: string | null,
    server: string | null
): EpochAction {
    if (!server) {
        return 'none';
    }
    if (!stored) {
        return 'store';
    }
    return stored === server ? 'none' : 'reset';
}

export function readStoredEpoch(): string | null {
    return storedEpoch.read();
}

/**
 * Remember this epoch as the one we are now in step with. MUST be called
 * BEFORE wiping the local database, not after: the wipe reloads the page, and
 * a device that reloads still remembering the old epoch would decide to wipe
 * all over again, forever.
 */
export function markEpochAccepted(epoch: string): void {
    // A browser refusing storage gets no epoch tracking, which beats a start
    // that fails — `write` swallows rather than throws.
    storedEpoch.write(epoch);
}

/** Ask the server, or null if it does not say — never throws, never blocks long. */
export async function fetchServerEpoch(): Promise<string | null> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    try {
        const response = await fetch('/api/meta', {
            cache: 'no-store',
            signal: controller.signal
        });
        if (!response.ok) {
            return null;
        }
        const data: unknown = await response.json();
        const epoch = (data as { dataEpoch?: unknown } | null)?.dataEpoch;
        return typeof epoch === 'string' && epoch !== '' ? epoch : null;
    } catch {
        return null;
    } finally {
        clearTimeout(timer);
    }
}

export interface EpochCheck {
    action: EpochAction;
    serverEpoch: string | null;
}

/**
 * The startup check: ask, decide, remember. Reports 'reset' WITHOUT acting on
 * it — the wipe is the caller's to present and perform, because the caller has
 * a screen to say so on. The epoch comes back with it.
 */
export async function checkDataEpoch(): Promise<EpochCheck> {
    const serverEpoch = await fetchServerEpoch();
    const action = decideEpochAction(readStoredEpoch(), serverEpoch);
    if (action === 'store' && serverEpoch) {
        markEpochAccepted(serverEpoch);
    }
    return { action, serverEpoch };
}

// --- a restore noticed mid-session -----------------------------------------
/**
 * The stream reports the epoch on every connect (routes/stream.ts). A restore
 * stops the server, so every device's stream reconnects afterwards — which is
 * how a tablet that is never reloaded finds out.
 */

type MismatchListener = (serverEpoch: string) => void;
const listeners = new Set<MismatchListener>();

export function onEpochMismatch(listener: MismatchListener): () => void {
    listeners.add(listener);
    return () => void listeners.delete(listener);
}

/** Report an epoch seen on the live stream; only a real mismatch is passed on. */
export function reportStreamEpoch(serverEpoch: string): void {
    if (decideEpochAction(readStoredEpoch(), serverEpoch) !== 'reset') {
        return;
    }
    for (const listener of listeners) {
        listener(serverEpoch);
    }
}
